/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './IconSwitch';

const IconSwitchManager: ComponentManagerFn<'IconSwitch'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'IconSwitch'>(platform, 'IconSwitch'),
    type: 'IconSwitch',
    name: 'Icon Switch',
    isChildAllowed: (page, item, childType) =>
      (['IconCase'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      switchCondition: {
        label: 'Switch Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
        helpText:
          'Must return a string value that will be used to find a matching Icon Case',
      },
      size: {
        label: 'Size',
        type: 'number',
        min: 12,
        max: 80,
      },
    },
    getDisplayText: (item) => item.props.switchCondition,
    category: 'Forms',
    icon: { iconName: 'icons' },
    description: `**Overview**<br/>
The Icon Switch component is a dynamic visualization tool that renders different icons based on a specific condition. It functions like a programmable "traffic light" or a "Switch/Case" statement in code: you define a central logic rule, and the component swaps the displayed icon to match the current result.
Use this component to create visual indicators for data that changes state—such as showing a green checkmark for "Completed" tasks, a yellow clock for "Pending," and a red flag for "Urgent."

**1. Switch Logic**<br/>
The core behavior of the component is driven by a single expression that determines which child icon is active.
* **Switch Expression:** A string expression that evaluates the current state of your data.
* **Mechanism:** The system evaluates this expression (e.g., returns "Approved"). It then looks through all child **Icon Case** components to find one with a matching **Case Value**.
* **Example:** \`$.row.status\` or \`$.project.priority\`.

**2. Defining Cases**<br/>
The Icon Switch does not render anything on its own; it requires **Icon Case** children to define the visual options.
* **Structure:** You must nest multiple **Icon Case** components inside the Icon Switch.
* **Matching:** The Switch evaluates the expression -> Finds the matching Case -> Renders that Case's icon and color.
* **Fallback:** If no case matches the expression, the component will render the **Icon Case** that has an empty **Case Value** (acting as a "Default" or "Else" block).

**3. Global Styling**<br/>
While individual colors are defined on the child cases (e.g., Red for Error, Green for Success), the physical size of the icon is controlled globally at the Switch level to ensure consistency.
* **Size:** Defines the dimension of the icon in pixels. (Range: Minimum 12px, Maximum 80px).
* **Consistency:** This setting applies to **all** child cases, preventing the UI from jumping or shifting layout when the icon changes.

**4. Best Use Cases**<br/>
* **Status columns in Grids:** Visually representing workflow states (Draft, Review, Published) without text.
* **Boolean Indicators:** Displaying a "Lock" icon if a record is locked and an "Edit" icon if it is unlocked.
* **Priority Visualization:** Showing varying urgency levels (High, Medium, Low) with distinct symbols.
* **File Type Previews:** Binding to a file extension field (e.g., \`$.file.ext\`) to show PDF, Excel, or Image icons dynamically.`,
  };
};

export default IconSwitchManager;
